import { STEPPER_GLOBAL_OPTIONS } from '@angular/cdk/stepper';
import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  Inject,
  OnInit,
  ViewChild,
} from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatSelectChange } from '@angular/material/select';
import {
  EMPTY,
  Observable,
  Subscription,
  catchError,
  filter,
  finalize,
  firstValueFrom,
  forkJoin,
  map,
  of,
  switchMap,
  throwError,
} from 'rxjs';
import { SpinnerOverlayComponent } from 'src/app/components/spinner-overlay/spinner-overlay.component';
import {
  Azienda,
  AziendaService,
  InfoFormVsme,
  TipoInstallazione,
} from 'src/app/services/azienda/azienda.service';
import { ImportazioniService } from 'src/app/services/importazioni/importazioni.service';
import {
  JsonFileService,
  Localita,
  Nace,
  Paese,
} from 'src/app/services/json-file/json-file.service';
import { LicenzeService } from 'src/app/services/licenze/licenze.service';
import { TemaService } from 'src/app/services/tema/tema.service';
import { Utente, UtenteService } from 'src/app/services/utente/utente.service';
import { UtilityService } from 'src/app/services/utility/utility.service';
import { InfoFormVsmeComponent } from './info-form-vsme/info-form-vsme.component';

@Component({
  selector: 'app-dialog-crea-azienda',
  templateUrl: './dialog-crea-azienda.component.html',
  styleUrls: ['./dialog-crea-azienda.component.scss'],
  standalone: false,
  providers: [
    {
      provide: STEPPER_GLOBAL_OPTIONS,
      useValue: { showError: true },
    },
  ],
})
export class DialogCreaAziendaComponent implements OnInit, AfterViewInit {
  @ViewChild(SpinnerOverlayComponent) spinnerOver!: SpinnerOverlayComponent;
  @ViewChild('infoFormVsme') infoFormVsme!: InfoFormVsmeComponent;

  public isPermessoSoloRead: boolean = false;

  public arrayUtentiAutoCompelte: Utente[] = [];

  public descrizioneErrore: string | undefined = undefined;
  public isIconaGuida: boolean = false;
  public urlLogo: string | undefined = undefined;

  private regionePreSelect: Localita | undefined = undefined;
  private provinciaPreSelect: Localita | undefined = undefined;
  private comunePreSelect: Localita | undefined = undefined;
  private nomeImpresa: string | undefined = undefined;
  public isModifica = false;
  public showAltriRecordDisponibili: boolean = false;

  public regioni: Localita[] = [];
  public filteredRegioni: Observable<Localita[]> | undefined;
  public province: Localita[] = [];
  public filteredProvince: Observable<Localita[]> | undefined;
  public comuni: Localita[] = [];
  public filteredComuni: Observable<Localita[]> | undefined;
  public infoFormVsmeFromAzienda: InfoFormVsme | undefined = undefined;
  public canSelectTipoInstallazione = this.utenteService.isPermessoAttivo('SELECT_INST_AZIENDA');

  public listaTipiInstallazione: TipoInstallazione[] = [];
  public isUtenteGo: boolean = false;
  private _subTema: Subscription | undefined;

  public imagePreview: string | ArrayBuffer | null = 'assets/images/logo/logo-sostenibile.png';
  public logoDaSalvare: File | undefined = undefined;
  public loadingUtente: boolean = false;

  /* Form Controls */
  public formAzienda = new FormGroup({
    /* ragione sociale */
    ragioneSociale: new FormControl<string>('', {
      nonNullable: true,
      validators: [Validators.required, UtilityService.blankValidator],
    }),

    /* piva */
    piva: new FormControl<string>('', {
      nonNullable: true,
      validators: [
        Validators.required,
        Validators.maxLength(11),
        Validators.minLength(11),
        Validators.pattern('^[0-9]*$'),
      ],
    }),
    codiciAteco: new FormControl<string[] | undefined>([], {
      validators: [Validators.required],
    }),
    /* indirizzo */
    indirizzo: new FormControl<string>('', {
      nonNullable: true,
    }),
    /* Regione */
    regione: new FormControl<string | Localita>('', {
      nonNullable: true,
    }),

    /* Comune */
    comune: new FormControl<string | Localita>('', {
      nonNullable: true,
    }),
    /* provincia */
    provincia: new FormControl<string | Localita>('', {
      nonNullable: true,
    }),
    /* cap */
    cap: new FormControl<string>('', {
      nonNullable: true,
      validators: [
        Validators.pattern('^[0-9]*$'),
        Validators.maxLength(5),
        Validators.minLength(5),
      ],
    }),
    /* sito web */
    sitoWeb: new FormControl<string>('', {
      nonNullable: true,
    }),
    /* descrizione */
    descrizione: new FormControl<string>('', {
      nonNullable: true,
    }),
    /* Id */
    id: new FormControl<string | undefined>(undefined, {
      nonNullable: true,
      validators: [],
    }),
    /* Dipendenti */
    dipendenti: new FormControl<number | undefined>(undefined, {
      nonNullable: true,
      validators: [Validators.pattern('^[0-9]*$'), Validators.min(0)],
    }),

    /* Fatturato */
    fatturato: new FormControl<number | undefined>(undefined, {
      nonNullable: true,
      validators: [Validators.pattern('^[0-9]*$'), Validators.min(0), Validators.max(999999999999)],
    }),
    /* CMSId */
    CMSId: new FormControl<string | undefined>(undefined, {
      nonNullable: true,
      validators: [],
    }),
    /* attivo */
    attivo: new FormControl<boolean>(true, {
      nonNullable: true,
    }),

    nome: new FormControl<string>('', {
      nonNullable: true,
      validators: [],
    }),
    email: new FormControl<string | undefined>(undefined, {
      nonNullable: true,
      validators: [Validators.email, UtilityService.emailValidator],
    }),
    tipoInstallazione: new FormControl<string | undefined>(undefined, {
      nonNullable: true,
      validators: [Validators.required],
    }),
    idUtente: new FormControl<Utente | undefined>(undefined, {
      nonNullable: true,
      validators: [Validators.required, UtilityService.blankValidator],
    }),
  });

  get isAtecoRequired(): boolean {
    return this.formAzienda.get('codiciAteco')?.hasValidator(Validators.required) || false;
  }

  get canReadVSME(): boolean {
    return this.utenteService.isPermessoAttivo('READ_VSME');
  }

  constructor(
    public dialogRef: MatDialogRef<DialogCreaAziendaComponent>,
    public aziendaService: AziendaService,
    private readonly utilityService: UtilityService,
    private readonly jsonFileService: JsonFileService,
    public utenteService: UtenteService,
    private readonly importazioniService: ImportazioniService,
    private readonly temaService: TemaService,
    private readonly cdRef: ChangeDetectorRef,
    private readonly licenzeService: LicenzeService,

    @Inject(MAT_DIALOG_DATA)
    public data: {
      azienda: Azienda;
      utente: Utente;
    },
  ) {
    if (this.data.azienda?.id && this.canReadVSME) {
      this.infoFormVsmeFromAzienda = {
        codiciNace: this.data.azienda.codiciNace,
        formaGiuridica: this.data.azienda.formaGiuridica,
        paeseOperazioni: this.data.azienda.paeseOperazioni,
        statoPatrimoniale: this.data.azienda.statoPatrimoniale,
        tabellaSiti: this.data.azienda.tabellaSiti,
        tipoAzienda: this.data.azienda.tipoAzienda,
      };
    }
  }

  ngOnDestroy(): void {
    this._subTema?.unsubscribe();
  }

  ngOnInit() {
    if (this.utenteService.infoUtente?.utente?.ruolo?.roleName === 'configuratore') {
      this.aziendaService.getListaTipiInstallazione().subscribe({
        next: (listaTipiInstallazione) => {
          this.listaTipiInstallazione = listaTipiInstallazione || [];
        },
        error: (err) => {
          console.error('ERRORE CARICAMENTO TIPI INSTALLAZIONE : ', err);
        },
      });
    }
  }

  ngAfterViewInit() {
    this.spinnerOver.show();

    if (this.data?.azienda?.id && this.data?.azienda?.idUtenteIncaricato) {
      this._getInfoUtente(this.data.azienda.idUtenteIncaricato)
        .then(() => {
          this.initFormAfterData();
        })
        .catch((error) => {
          console.error('Errore durante il recupero info utente:', error);
        })
        .finally(() => {
          this.spinnerOver.hide();
        });
    } else {
      this.initFormAfterData();

      this.spinnerOver.hide();
    }
  }

  private initFormAfterData() {
    this.ctrTipoTema();

    this.spinnerOver.show();
    this.valoriIniziali().subscribe({
      next: (result) => {
        if (result) {
          this.regioniAutoComplete();

          this.isModifica = !!this.formAzienda.get('id')?.value;
          if (!this.isModifica) {
            this.formAzienda
              .get('nome')
              ?.addValidators([Validators.required, UtilityService.blankValidator]);
            this.formAzienda
              .get('email')
              ?.addValidators([
                Validators.email,
                UtilityService.emailValidator,
                Validators.required,
              ]);
          } else {
            this.formAzienda.get('nome')?.clearValidators();
            this.formAzienda.get('email')?.clearValidators();
          }

          this.formAzienda.get('nome')?.updateValueAndValidity();
          this.formAzienda.get('email')?.updateValueAndValidity();
        }
        this.spinnerOver.hide();
      },
      error: (err) => {
        console.error('Errore nella subscribe:', err);
        this.spinnerOver.hide();
      },
    });

    const isEdit = this.utenteService.isPermessoAttivo('EDIT_AZIENDA');
    const isCreate = this.utenteService.isPermessoAttivo('CREATE_AZIENDA');
    this.isPermessoSoloRead = !isEdit && !isCreate;

    if (this.isPermessoSoloRead) {
      this.formAzienda.disable();
    }

    if (!this.data?.azienda?.id && this.data?.utente?.id) {
      this.gestioneTipoInstallazione(this.data?.utente?.ruolo.roleName);
    }

    if (
      this.utenteService.infoUtente?.utente?.ruolo?.roleName === 'configuratore' &&
      !this.data?.azienda &&
      !this.data?.utente?.id
    ) {
      this.loadingUtente = true;
      this.utenteService
        .getUtenti(0, 11)
        .pipe(finalize(() => (this.loadingUtente = false)))
        .subscribe({
          next: (esito) => {
            this.arrayUtentiAutoCompelte = esito.content;
          },
          error: (err) => {
            console.error(err);
          },
        });
    } else {
      this.formAzienda.get('idUtente')?.disable();
    }

    this.cdRef.detectChanges();

    if (this.utenteService.infoUtente?.utente?.ruolo?.roleName === 'utente_premium') {
      this.formAzienda.get('piva')?.disable();
      this.formAzienda.get('ragioneSociale')?.disable();
      this.formAzienda.get('codiciAteco')?.disable();
      if (this.canReadVSME) {
        this.infoFormVsme.formInfoVsme.get('codiciNace')?.disable();
        this.infoFormVsme.formInfoVsme.get('paeseOperazioni')?.disable();
      }
    }
  }

  private gestioneTipoInstallazione(ruolo?: string) {
    const isRuoloGo = ruolo?.includes('_go');

    // Riferimenti ai controlli principali
    const tipoInstallazioneCtrl = this.formAzienda.get('tipoInstallazione');
    const codiciAtecoCtrl = this.formAzienda.get('codiciAteco');

    if (isRuoloGo) {
      tipoInstallazioneCtrl?.setValue('SOSTENIBILE_GO');
      codiciAtecoCtrl?.setValue([]);
      codiciAtecoCtrl?.clearValidators();
    } else {
      tipoInstallazioneCtrl?.setValue('SOSTENIBILE_CLOUD');
      codiciAtecoCtrl?.setValidators(Validators.required); // setValidators è più sicuro se vuoi resettare lo stato
    }

    // Gestione dinamica VSME
    if (this.canReadVSME) {
      const vsmeForm = this.infoFormVsme.formInfoVsme;
      const controlsVSME = [vsmeForm.get('codiciNace'), vsmeForm.get('paeseOperazioni')];

      controlsVSME.forEach((ctrl) => {
        if (isRuoloGo) {
          ctrl?.setValue('');
          ctrl?.clearValidators();
        }
        ctrl?.updateValueAndValidity();
      });
    }

    // update form
    tipoInstallazioneCtrl?.disable();
    codiciAtecoCtrl?.updateValueAndValidity();
  }

  compareById(elem1: any, elem2: any): boolean {
    return elem1.id === elem2.id;
  }

  /*GESTIONE AUTOCOMPLETE DEI KPI NELLA TAB RISPOSTE  */
  getUtentiAutoComplete = (id: any, txtRicerca: string): Observable<Utente[]> => {
    if (id) {
      const records = this.arrayUtentiAutoCompelte.filter(
        (option) => option.id?.toLowerCase() === id,
      );
      return of(records);
    } else if (txtRicerca) {
      this.loadingUtente = true;

      return this.utenteService.getUtenti(0, 11, txtRicerca, [], []).pipe(
        finalize(() => (this.loadingUtente = false)),
        map((esito) => {
          this.arrayUtentiAutoCompelte = esito.content;
          return this._filterUtenti(txtRicerca as string); // Applica il filtro
        }),
        catchError((err) => {
          console.error(err);
          return of([]); // Ritorna un array vuoto in caso di errore
        }),
      );
    } else {
      const records = this.arrayUtentiAutoCompelte.slice();
      return of(records);
    }
  };

  displayFnUtente(utente: Utente): string {
    return (
      utente && (utente.firstName + ' ' + utente.lastName).toUpperCase() + ' - ' + utente.username
    );
  }

  private _filterUtenti(ricerca: string): Utente[] {
    const filterValue = ricerca.toLowerCase();
    return this.arrayUtentiAutoCompelte.filter(
      (option) =>
        option.firstName?.toLowerCase().includes(filterValue) ||
        option.lastName?.toLowerCase().includes(filterValue) ||
        option.username?.toLowerCase().includes(filterValue),
    );
  }

  onSelectionChangeTipoInstallazione(event: MatSelectChange) {
    const isGo = event?.value === 'SOSTENIBILE_GO';
    const codiciAtecoCtrl = this.formAzienda.get('codiciAteco');

    if (isGo) {
      codiciAtecoCtrl?.setValue([]);
      codiciAtecoCtrl?.clearValidators();
    } else {
      codiciAtecoCtrl?.setValidators(Validators.required);
    }
    codiciAtecoCtrl?.updateValueAndValidity();

    // Gestione centralizzata VSME
    if (this.canReadVSME) {
      const vsmeForm = this.infoFormVsme.formInfoVsme;
      const vsmeControls = [vsmeForm.get('codiciNace'), vsmeForm.get('paeseOperazioni')];

      vsmeControls.forEach((ctrl) => {
        if (ctrl) {
          if (isGo) {
            ctrl.setValue('');
            ctrl.clearValidators();
          } else {
            ctrl.setValidators(Validators.required);
          }
          ctrl.updateValueAndValidity();
        }
      });
    }
  }

  onKeyDownDip(event: KeyboardEvent): void {
    if (event.key === ',' || event.key === '.') {
      // Impedisce l'inserimento del carattere "-"
      event.preventDefault();
    }
  }

  onKeyDown(event: KeyboardEvent): void {
    // Verifica se il tasto premuto è il carattere "-"
    if (event.key === '-') {
      // Impedisce l'inserimento del carattere "-"
      event.preventDefault();
    }
  }

  ctrElem(event: any, input: string) {
    const valoreInput = event.target.value;

    switch (input) {
      case 'regione':
        if (!this.regioni.includes(valoreInput)) {
          this.formAzienda.controls.regione.setValue('');
        }
        break;
      case 'provincia':
        if (!this.province.includes(valoreInput)) {
          this.formAzienda.controls.provincia.setValue('');
        }
        break;
      case 'comune':
        if (!this.comuni.includes(valoreInput)) {
          this.formAzienda.controls.comune.setValue('');
        }
        break;

      default:
        break;
    }
  }

  /* displayfn e filter per regione-province--comuni */
  displayFn(localita: Localita): string {
    return localita && localita.nome ? localita.nome : '';
  }

  compareLocalita(localita1: Localita, localita2: Localita): boolean {
    return localita1.id === localita2.id; // Sostituisci "id" con la proprietà univoca delle localita
  }

  /* REGIONI */
  getRegioni = (id: any, txtRicerca: string) => {
    let records: Localita[] = [];

    if (id) {
      records = this.regioni.filter((option) => option.id?.toLowerCase() === id);
    } else if (txtRicerca) {
      records = this._filterRegioni(txtRicerca as string);
    } else {
      records = this.regioni.slice();
    }

    return of(records);
  };

  /* Province */
  getProvince = (id: any, txtRicerca: string) => {
    let records: Localita[] = [];

    if (id) {
      records = this.province.filter((option) => option.id?.toLowerCase() === id);
    } else if (txtRicerca) {
      records = this._filterProvince(txtRicerca as string);
    } else {
      records = this.province.slice();
    }

    return of(records);
  };
  /* Comune */
  getComune = (id: any, txtRicerca: string) => {
    let records: Localita[] = [];

    if (id) {
      records = this.comuni.filter((option) => option.id?.toLowerCase() === id);
    } else if (txtRicerca) {
      records = this._filterComune(txtRicerca as string);
    } else {
      records = this.comuni.slice();
    }

    return of(records);
  };

  private _filterRegioni(regione: string): Localita[] {
    const filterValue = regione.toLowerCase();
    return this.regioni.filter((option) => option.nome?.toLowerCase().includes(filterValue));
  }

  private _filterProvince(province: string): Localita[] {
    const filterValue = province.toLowerCase();
    return this.province.filter((option) => option.nome?.toLowerCase().includes(filterValue));
  }

  private _filterComune(comune: string): Localita[] {
    const filterValue = comune.toLowerCase();
    return this.comuni.filter((option) => option.nome?.toLowerCase().includes(filterValue));
  }

  /**
   * Metodo che prende le unità di misura
   */
  public getProvinciaAutoComplete(record: Localita) {
    if (record.id) {
      this.provinceAutoComplete(record.id);
    }
  }
  /**
   * Metodo che prende le unità di misura
   */
  public getComuneAutoComplete(record: Localita) {
    if (record.id) {
      this.comuniAutoComplete(record.id);
    }
  }
  /* ------------------------------------------------------------------------------------ */

  /**
   * Metodo che mi ritorna l'id dell'utente a cui assocciare l'azienda
   * @returns
   */
  getIdUtente() {
    if (this.data?.utente?.id) {
      return this.data?.utente?.id;
    } else {
      return this.formAzienda.get('idUtente')?.value?.id;
    }
  }

  /**
   * Funzione di salvataggio di una nuova Azienda
   * @returns
   */
  public async salva(): Promise<boolean> {
    if (this.formAzienda.valid) {
      const formAz = this.formAzienda.getRawValue();
      let infoVsme = {} as any;
      const id = this.formAzienda.get('id')?.value;

      if (this.canReadVSME) {
        infoVsme = this.infoFormVsme.formInfoVsme.getRawValue();
      }

      // 2. Build l'oggetto mappato
      const datiMappati = {
        id: formAz.id,
        cmsId: formAz.CMSId,
        ragioneSociale: formAz.ragioneSociale,
        piva: formAz.piva,
        codiciAteco: formAz.codiciAteco,

        codiciNace:
          infoVsme.codiciNace && (infoVsme.codiciNace as Nace)._id
            ? [(infoVsme.codiciNace as Nace)._id]
            : [],

        paeseOperazioni:
          infoVsme.paeseOperazioni && (infoVsme.paeseOperazioni as Paese).countryCode
            ? [(infoVsme.paeseOperazioni as Paese).countryCode]
            : [],

        indirizzo: formAz.indirizzo,
        regione: (formAz.regione as Localita)?.nome,
        comune: (formAz.comune as Localita)?.nome,
        provincia: (formAz.provincia as Localita)?.nome,
        cap: formAz.cap,

        tipoAzienda: infoVsme.tipoAzienda || null,
        dipendenti: formAz.dipendenti,
        fatturato: formAz.fatturato,
        statoPatrimoniale: infoVsme.statoPatrimoniale || null,

        descrizione: formAz.descrizione,
        sitoWeb: formAz.sitoWeb,
        attivo: formAz.attivo,
        urlLogo: this.urlLogo,
        tipoInstallazione: formAz.tipoInstallazione,
        formaGiuridica: infoVsme.formaGiuridica || null,

        tabellaSiti: this.infoFormVsme?.tabellaSitiCmp?.salva() || null,
        idUtenteIncaricato: formAz.id ? formAz.idUtente?.id : '',
      };

      if (typeof datiMappati?.codiciNace === 'string') {
        datiMappati.codiciNace = [(datiMappati.codiciNace as string).trim()];
      }

      if (typeof datiMappati?.paeseOperazioni === 'string') {
        datiMappati.paeseOperazioni = [(datiMappati.paeseOperazioni as string).trim()];
      }

      let obsSalvataggio: Observable<boolean>;

      const oggettoSalvataggioAzienda = {
        azienda: datiMappati,
        idUtente: this.getIdUtente(),
      };

      if (id) {
        obsSalvataggio = this.aziendaService.putAzienda(id, datiMappati).pipe(map(() => true)); // Se non dà errore tutto ok, si torna true
      } else {
        obsSalvataggio = this.aziendaService.postAzienda(oggettoSalvataggioAzienda).pipe(
          switchMap((azienda) => {
            if (azienda?.id) {
              this.formAzienda.get('id')?.setValue(azienda.id);
              return of(azienda.id); // Restituisci direttamente l'idAzienda
            } else {
              return throwError(() => 'Nessuna informazione ricevuta dal server');
            }
          }),
          switchMap((idAzienda) => {
            return this.aziendaService.postFigureAz(idAzienda, {
              nome: this.formAzienda.get('nome')?.value,
              email: this.formAzienda.get('email')?.value,
              figura: 'Referente',
              idAzienda,
            });
          }),
        );
      }

      return firstValueFrom(
        obsSalvataggio.pipe(
          map((result: any) => {
            this.aziendaService.caricaAziende();

            this.utilityService.openSnackBar('Salvataggio effettuato ', '', {
              duration: 2000,
              panelClass: ['success-snackbar'],
            });

            return true;
          }),
          catchError((err) => {
            let msg: string;
            if (err.status === 403 && this.isUtenteGo) {
              msg = "Non puoi creare un\'altra azienda Sostenibile GO";
            } else {
              msg = err?.error?.message;
            }

            this.utilityService.openDialog({
              titolo: 'Attenzione',
              descrizione: msg,
              bottoni: [{ nome_btn: 'Chiudi' }],
            });

            return of(false);
          }),
        ),
      );
    } else {
      Object.values(this.formAzienda.controls).forEach((control) => {
        control.markAsTouched();
      });
    }

    return false;
  }

  /**
   * Metodo che mi ritorna lista delle regioni
   */
  public regioniAutoComplete() {
    this.formAzienda.controls.provincia.disable();
    this.formAzienda.controls.comune.disable();
    this.jsonFileService.getRegioni().subscribe({
      next: (regioni: Localita[]) => {
        this.regioni = regioni;

        const regioneControl = this.formAzienda.get('regione');

        if (this.regionePreSelect) {
          regioneControl?.setValue(this.regionePreSelect);
          this.regionePreSelect = undefined;
        }
      },
      error: (err: any) => {
        console.error('errore get regioni', err);
      },
    });
  }

  /**
   * Metodo che ritorna lista di province filtrate per id regione
   * @param id id della regione
   */
  public provinceAutoComplete(id: string) {
    this.formAzienda.controls.provincia.enable();
    this.formAzienda.controls.provincia.setValue('');
    this.formAzienda.controls.comune.setValue('');
    this.jsonFileService.getProvince(id).subscribe({
      next: (province: Localita[]) => {
        this.province = province;
        const provinciaControl = this.formAzienda.get('provincia');

        if (this.provinciaPreSelect) {
          provinciaControl?.setValue(this.provinciaPreSelect);
          this.provinciaPreSelect = undefined;
        }
      },
      error: (err: any) => {
        console.error('errore get province', err);
      },
    });
  }

  /**
   * Metodo che ritorna lista dei comuni filtrate per id provincia
   * @param id id provincia
   */
  public comuniAutoComplete(id: string) {
    this.formAzienda.controls.comune.enable();
    this.formAzienda.controls.comune.setValue('');
    this.jsonFileService.getComuni(id).subscribe({
      next: (comuni: Localita[]) => {
        this.comuni = comuni;
        const comuneControl = this.formAzienda.get('comune');
        if (this.comunePreSelect) {
          comuneControl?.setValue(this.comunePreSelect);
          this.comunePreSelect = undefined;
        }
      },
      error: (err: any) => {
        console.error('errore get comuni', err);
      },
    });
  }

  public valoriIniziali(): Observable<boolean> {
    /* valori iniziali in modifica per autocomplete */
    let regioneIniziale: string = '';
    let provinciaIniziale: string = '';
    let comuneIniziale: string = '';

    if (this.data?.azienda) {
      Object.keys(this.data.azienda).forEach((value, index, array) => {
        /*      if (value === 'codiciAteco') {
          const record = this.data.azienda as any;
          const valoreAteco = record?.codiciAteco?.length ? record?.codiciAteco[0] : '';
          atecoIniziale = valoreAteco;
        } else */ if (value === 'regione') {
          regioneIniziale = this.data.azienda.regione;
        } else if (value === 'provincia') {
          provinciaIniziale = this.data.azienda.provincia;
        } else if (value === 'comune') {
          comuneIniziale = this.data.azienda.comune;
        } else if (value === 'urlLogo' && this.data.azienda.urlLogo) {
          this.urlLogo = this.data.azienda.urlLogo;
        } else {
          this.formAzienda.get(value)?.setValue((this.data.azienda as any)[value]);
        }
      });

      this.nomeImpresa = this.formAzienda.get('ragioneSociale')?.value;

      /*  Definisco un oggetto che contiene un gruppo di Observalbe. Questo oggetto mi servirà per
      poi fare una forkJoin.
      Ogni observable prende una richiesta (regioni - province - comune)*/

      const obsPerForkJoin: {
        regione: Observable<Localita[]>;
        provincia: Observable<Localita[]>;
        comune: Observable<Localita[]>;
      } = {
        regione: of([]),
        provincia: of([]),
        comune: of([]),
      };

      if (regioneIniziale) {
        obsPerForkJoin.regione = this.jsonFileService.getRegioniByNome(regioneIniziale);

        if (provinciaIniziale) {
          obsPerForkJoin.provincia = this.jsonFileService.getProvinceByNome(provinciaIniziale);

          if (comuneIniziale) {
            obsPerForkJoin.comune = this.jsonFileService.getComuniByNome(comuneIniziale);
          }
        }
      }
      this.spinnerOver.show();
      /* 
      La forkJoin è un operatore RXJS che viene usato quando bisogna eseguire un gruppo di Observable
      e quindi ricevere risposta da tutti gli observable del gruppo.
      In questo caso mi esegue tutti gli Observable del gruppo obsPerForkJoin, dove mi ritorna il risultato della richiesta che 
      ho assegnato nel gruppo ad ogni Observable. 
       */
      return forkJoin(obsPerForkJoin).pipe(
        map((result) => {
          this.regionePreSelect = result.regione[0] || undefined;
          this.provinciaPreSelect = result.provincia[0] || undefined;
          this.comunePreSelect = result.comune[0] || undefined;
          return true;
        }),
        catchError((err) => {
          console.error(err);
          return of(false);
        }),
        finalize(() => this.spinnerOver.hide()),
      );
    } else {
      return of(true);
    }
  }

  /**
   * Importazione di un logo
   */
  public async fileSelezionato(file: File[]) {
    // Controllo se il file > 1mb
    if (file[0].size > 1048576) {
      const dialog = await this.utilityService.openDialog({
        titolo: 'Dimensione file eccessiva',
        descrizione: 'La dimensione del file supera 1 Mb, la vuoi ridimensionare',
        bottoni: [
          {
            nome_btn: 'No',
            id_btn: 'N',
          },
          {
            nome_btn: 'Si',
            id_btn: 'S',
          },
        ],
      });

      const valDialog = await firstValueFrom(dialog.beforeClosed());
      if (valDialog === 'N') {
        return;
      }
    }

    if (
      file[0].type === 'image/jpeg' ||
      file[0].type === 'image/jpg' ||
      file[0].type === 'image/png' ||
      file[0].type === 'image/gif' ||
      file[0].type === 'image/svg+xml'
    ) {
      this.isIconaGuida = false;

      this.logoDaSalvare = file[0];
      // Creo un'anteprima dell'immagine
      const reader = new FileReader();
      reader.onload = () => {
        this.imagePreview = reader.result;
        this.urlLogo = undefined;
      };
      reader.readAsDataURL(file[0]);
    } else {
      this.utilityService.openSnackBar(
        " Attenzione! Il file d'importazione deve essere un file immagine (.png .jpeg .jpg .gif .svg)",
        '',
        {
          duration: 5000,
          panelClass: ['red-snackbar'],
        },
      );
      this.descrizioneErrore =
        " Attenzione! Il file d'importazione deve essere un file immagine (.png, .jpeg, .jpg, .gif, .svg)";
      this.isIconaGuida = true;
    }
  }

  /**
   * Metodo per eliminazione del logo aziendale.
   * Viene rimosso da google cloud storage
   */
  public eliminaLogo() {
    if (this.logoDaSalvare) {
      this.imagePreview = 'assets/images/logo/logo-sostenibile.png';
      this.logoDaSalvare = undefined;
    } else {
      const idAzienda = this.formAzienda.get('id')?.value;
      if (idAzienda) {
        this.spinnerOver.show();

        this.importazioniService.deleteLogoAzienda(idAzienda).subscribe({
          next: (esito) => {
            this.spinnerOver.hide();
            this.urlLogo = undefined;
            this.utilityService.openSnackBar('Logo rimosso', '', {
              duration: 2000,
              panelClass: ['red-snackbar'],
            });
          },
          error: (err) => {
            console.error(err);
            this.spinnerOver.hide();
          },
        });
      }
    }
  }
  /**
   * Controlla il tipo di tema. Se è un go devo disabilitare i validatori su ateco e nace e paese
   */
  ctrTipoTema() {
    this._subTema = this.temaService.temaUpdated.subscribe((tipoInstallazione) => {
      this.imagePreview = this.temaService.logoPath;

      const tipoAziendaEdit = this.data?.azienda?.tipoInstallazione; // se sono  utente cfg e voglio modificare un azienda go

      if (!this.utenteService.isPermessoAttivo('SELECT_INST_AZIENDA')) {
        this.formAzienda.get('tipoInstallazione')?.clearValidators();
      }

      if (tipoInstallazione === 'SOSTENIBILE_GO' || tipoAziendaEdit === 'SOSTENIBILE_GO') {
        this.isUtenteGo = true;
        this.formAzienda.get('codiciAteco')?.clearValidators();
        if (this.canReadVSME) {
          this.infoFormVsme.formInfoVsme.get('codiciNace')?.clearValidators();
          this.infoFormVsme.formInfoVsme.get('paeseOperazioni')?.clearValidators();
        }
      } else {
        this.isUtenteGo = false;
        this.formAzienda.get('codiciAteco')?.addValidators(Validators.required);
      }
      this.formAzienda.get('codiciAteco')?.updateValueAndValidity();
      this.formAzienda.get('tipoInstallazione')?.updateValueAndValidity();
      if (this.canReadVSME) {
        this.infoFormVsme.formInfoVsme.get('codiciNace')?.updateValueAndValidity();
        this.infoFormVsme.formInfoVsme.get('paeseOperazioni')?.updateValueAndValidity();
      }
    });
  }

  public async salvaAzienda() {
    try {
      this.spinnerOver.show();

      const resultAzienda = await this.salva();
      if (!resultAzienda) {
        return;
      }
      if (this.logoDaSalvare) {
        const resultLogo = await this.importaLogo();
        if (resultLogo) {
          this.dialogRef.close(this.formAzienda.get('id')?.value);
        }
      } else {
        this.dialogRef.close(this.formAzienda.get('id')?.value);
      }
    } catch (error) {
      console.error("Errore durante il salvataggio dell'azienda o l'importazione del logo", error);
    } finally {
      this.spinnerOver.hide();
    }
  }

  async importaLogo(): Promise<boolean> {
    const idAzienda = this.formAzienda.get('id')?.value;
    if (idAzienda && this.logoDaSalvare) {
      return firstValueFrom(
        this.importazioniService.importLogo(this.logoDaSalvare, {}, idAzienda).pipe(
          filter((res) => res?.type === 4),
          map((result: any) => {
            console.warn(result);

            if (result.body) {
              this.urlLogo = result.body.urlLogo;
            }
            return true;
          }),
          catchError((err) => {
            console.error(err);
            this.utilityService.openDialog({
              titolo: 'Attenzione',
              descrizione: err?.error?.message || "Errore nell\'importazione",
              bottoni: [{ nome_btn: 'Chiudi' }],
            });
            return of(false);
          }),
        ),
      );
    } else {
      return false;
    }
  }

  // Gestione cancellazione in mat select
  hasValue(formControlName: string) {
    if (
      this.data.azienda ||
      !this.formAzienda.get(formControlName)?.value ||
      this.formAzienda.get(formControlName)?.status === 'DISABLED'
    ) {
      return true;
    }
    return false;
  }

  clearInput(formControlName: string, event?: Event): void {
    const field = this.formAzienda.get(formControlName);

    if (!field?.disabled) {
      field?.reset();
      field?.markAsDirty();
      field?.markAsTouched();
    }
    if (event) event.stopPropagation();
  }

  visualizzaCodiceAteco() {
    if (
      (this.formAzienda.get('tipoInstallazione')?.value !== 'SOSTENIBILE_GO' && !this.isUtenteGo) ||
      (this.utenteService.infoUtente?.utente?.ruolo?.roleName === 'configuratore' &&
        this.formAzienda.get('tipoInstallazione')?.value !== 'SOSTENIBILE_GO')
    ) {
      return true;
    }
    return false;
  }

  visualizzaUtente() {
    const isConfiguratore =
      this.utenteService.infoUtente?.utente?.ruolo?.roleName === 'configuratore';

    const isEditAzienda = !!this.data?.azienda;

    const isAziendaCreateFromUtenteList = !!this.data?.utente?.id;

    if (isEditAzienda) {
      this.formAzienda.get('idUtente')?.disable();
    }

    // Se configuratore visualizzo sempre il form utente
    if (isConfiguratore && !isAziendaCreateFromUtenteList) {
      return true;
    }

    //
    if (!isConfiguratore && isEditAzienda) {
      return true;
    }

    return false;
  }

  private async _getInfoUtente(idUtente: string): Promise<boolean> {
    this.loadingUtente = true;
    try {
      const result: Utente = await firstValueFrom(this.utenteService.getUtente(idUtente));
      this.arrayUtentiAutoCompelte = [result];
      this.formAzienda.get('idUtente')?.setValue(result);
      return true;
    } catch (error) {
      console.error(error);
      return false;
    } finally {
      this.loadingUtente = false;
    }
  }

  changeSelectionUtente(utente: Utente) {
    if (!utente?.id) {
      return;
    }

    this.spinnerOver.show();

    this.licenzeService
      .getUserHasLicenseIdUtente(utente.id, 'Azienda')
      .pipe(
        map(() => utente), // restituisce l'utente originale
        catchError((error) => {
          const errorMessage = error?.error?.message || 'Si è verificato un errore.';
          this.utilityService.openDialog({
            titolo: 'Attenzione',
            descrizione: errorMessage,
            bottoni: [{ nome_btn: 'Ok' }],
          });

          // reset campo form
          this.formAzienda.get('idUtente')?.setValue(undefined);

          return EMPTY; // blocca il flusso
        }),
        finalize(() => this.spinnerOver.hide()),
      )
      .subscribe((utenteValido) => {
        const ruolo = utenteValido?.ruolo?.roleName;
        this.gestioneTipoInstallazione(ruolo);
      });
  }
}
